import { forwardRef } from 'react'
import { cn } from '../helpers/classnames'

export interface SimpleGridProps extends React.HTMLAttributes<HTMLDivElement> {
  columns?: number | { base?: number; sm?: number; md?: number; lg?: number; xl?: number }
  spacing?: number | string
  spacingX?: number | string
  spacingY?: number | string
}

/**
 * SimpleGrid component - Responsive grid layout
 * Replaces Chakra UI's SimpleGrid component
 */
export const SimpleGrid = forwardRef<HTMLDivElement, SimpleGridProps>(
  ({ children, columns = 1, spacing, spacingX, spacingY, className, ...rest }, ref) => {
    // Build responsive column classes
    const getColumnClasses = () => {
      if (typeof columns === 'number') {
        return `grid-cols-${columns}`
      }
      
      const classes = []
      if (columns.base) classes.push(`grid-cols-${columns.base}`)
      if (columns.sm) classes.push(`sm:grid-cols-${columns.sm}`)
      if (columns.md) classes.push(`md:grid-cols-${columns.md}`)
      if (columns.lg) classes.push(`lg:grid-cols-${columns.lg}`)
      if (columns.xl) classes.push(`xl:grid-cols-${columns.xl}`)
      
      return classes.join(' ')
    }
    
    // Build gap classes
    const getGapClasses = () => {
      const classes = []
      
      if (spacing) {
        const gapValue = typeof spacing === 'number' ? spacing : parseInt(spacing)
        classes.push(`gap-${gapValue}`)
      } else {
        if (spacingX) {
          const gapXValue = typeof spacingX === 'number' ? spacingX : parseInt(spacingX)
          classes.push(`gap-x-${gapXValue}`)
        }
        if (spacingY) {
          const gapYValue = typeof spacingY === 'number' ? spacingY : parseInt(spacingY)
          classes.push(`gap-y-${gapYValue}`)
        }
      }
      
      return classes.join(' ')
    }
    
    return (
      <div
        ref={ref}
        className={cn('grid', getColumnClasses(), getGapClasses(), className)}
        {...rest}
      >
        {children}
      </div>
    )
  }
)

SimpleGrid.displayName = 'SimpleGrid'